import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Layout } from "./components/layout/Layout";
import { PageLoader } from "./components/ui/Loader";
import { AuthProvider } from "./contexts/AuthContext";
import { ToastProvider } from "./contexts/ToastContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { ScrollToTopOnRouteChange } from "./components/layout/ScrollToTopOnRouteChange";
import { initializeMockData } from "./data/mockData";

// Lazy load public pages
const Home = lazy(() => import("./pages/Home"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const CaseStudy = lazy(() => import("./pages/CaseStudy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));

// NOTE: The admin panel now lives in the separate top-level `admin/` folder
// (its own React app that integrates the Techoside API). Its routes were
// removed from this website app — see ../../admin.

// Lazy load client portal pages
const ClientLogin = lazy(() => import("./client-portal/pages/ClientLogin"));
const ClientLayout = lazy(
  () => import("./client-portal/components/ClientLayout"),
);
const ClientDashboard = lazy(
  () => import("./client-portal/pages/ClientDashboard"),
);
const ClientProject = lazy(() => import("./client-portal/pages/ClientProject"));
const ClientDocuments = lazy(
  () => import("./client-portal/pages/ClientDocuments"),
);
const ClientInvoices = lazy(
  () => import("./client-portal/pages/ClientInvoices"),
);
const ClientMessages = lazy(
  () => import("./client-portal/pages/ClientMessages"),
);

function App() {
  useEffect(() => {
    // Initialize mock data on first load
    initializeMockData();
  }, []);

  return (
    <AuthProvider>
      <ToastProvider>
      <ScrollToTopOnRouteChange />
      <Suspense fallback={<PageLoader />}>
        <AnimatePresence mode="wait">
          <Routes>
            {/* Public Website Routes */}
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/case-study" element={<CaseStudy />} />
              <Route
                path="/terms-and-conditions"
                element={<TermsAndConditions />}
              />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              {/* Catch all - real 404 page (noindex) instead of a redirect */}
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Client Login */}
            <Route path="/client-login" element={<ClientLogin />} />

            {/* Client Portal Routes */}
            <Route
              path="/client"
              element={
                <ProtectedRoute allowedUserType="client">
                  <ClientLayout />
                </ProtectedRoute>
              }
            >
              <Route
                index
                element={<Navigate to="/client/dashboard" replace />}
              />
              <Route path="dashboard" element={<ClientDashboard />} />
              <Route path="project" element={<ClientProject />} />
              <Route path="documents" element={<ClientDocuments />} />
              <Route path="invoices" element={<ClientInvoices />} />
              <Route path="messages" element={<ClientMessages />} />
            </Route>
          </Routes>
        </AnimatePresence>
      </Suspense>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
